import React, { memo, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import useHeaderBar from '../../hooks/useHeaderBar';
import styles from './styles.module.scss';
import CaretIcon from '@/components/component-library/Icon/types/Caret';
import SearchIcon from '@/components/component-library/Icon/types/Search';
import OptionTripleDotIcon from '@/components/component-library/Icon/types/OptionTripleDot';
import Text from '@/components/component-library/Text';
import ProfileImage from '@/components/component-library/Image/KeakieImageProfile';
import useUser from '@/hooks/api/useUser';
import { classes } from '@/helpers/styling';
import { createNameString } from '@/helpers/pages/profile';
import {
  DISCOVER_KEY,
  LIBRARY_KEY,
  PROFILE_KEY
} from '@/config/routeKeys';
import useActiveRoutes from '@/hooks/routing/useActiveRoutes';
import SignInLink from '@/components/app/SignInLink/SignInLink';
import pageKeys from '@/helpers/routing/constants';
import Link from '@/components/component-library/Link';
import Span from '@/components/component-library/Span';
import DropDownSelectOptions from '@/components/component-library/DropDownSelect/DropDownSelectOptions';
import useFocusAndBlur from '@/hooks/browser/useFocusAndBlur';
import {
  EXT_LINK_ABOUT, EXT_LINK_ANDROID_APP, EXT_LINK_INSTAGRAM, EXT_LINK_IOS_APP, EXT_LINK_NEWSROOM
} from '@/config/external-links';
import useLanguageEnabled from '@/hooks/language/useLanguageEnabled';
import useLanguage from '@/hooks/language/useLanguage';

const CHOOSE_LANGUAGE_DROP_DOWN_ID = 'choose-language';

const createDropDownOptions = () => [
  {
    id: 'about',
    text: 'What is Keakie?',
    href: EXT_LINK_ABOUT,
    isExternalHref: true
  },
  {
    id: 'news',
    text: 'News',
    href: EXT_LINK_NEWSROOM,
    isExternalHref: true
  },
  {
    id: 'instagram',
    text: 'Instagram',
    href: EXT_LINK_INSTAGRAM,
    isExternalHref: true
  },
  {
    id: 'android-download-link',
    text: 'Android Download',
    href: EXT_LINK_ANDROID_APP,
    isExternalHref: true
  },
  {
    id: 'ios-download-link',
    text: 'iOS Download',
    href: EXT_LINK_IOS_APP,
    isExternalHref: true
  },
  // {
  //   id: CHOOSE_LANGUAGE_DROP_DOWN_ID,
  //   text: 'Choose Language'
  // }
];

const HeaderOption = memo(React.forwardRef(({
  children,
  href,
  open,
  active,
  onClick,
  hasCaret,
  text,
  label,
  className,
  disableHover,
  ...props
}, ref) => {
  let childContent = null;

  if (children) {
    childContent = (
      <Span
        className={ classes('header-option-content', className) }
      >
        { children }
        <Span className={ classes('active-line', { '--is-active': active }) } />
      </Span>
    );
  } else if (text) {
    childContent = (
      <Text
        bold
        className={ classes('header-option-content', className) }
      >
        { text }
        <Span className={ classes('active-line', { '--is-active': active }) } />
      </Text>
    );
  }

  const child = (
    <>
      { childContent }
      { hasCaret && (
        <CaretIcon
          className='caret-down'
        />
       ) }
    </>
  );

  return (
    <div
      className={ classes('desktop-header-option', {
        '--is-active': active,
        '--is-open': open,
        '--disable-hover': disableHover
      }) }
      onClick={ onClick }
      { ...(onClick ? { role: 'button' } : {}) }
      { ...(href ? {} : { 'aria-label': label || text }) }
      ref={ ref }
      { ...props }
    >
      { !href ? child : <Link href={ href }>{ child }</Link> }
    </div>
  );
}));

HeaderOption.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]),
  href: PropTypes.string,
  open: PropTypes.bool,
  active: PropTypes.bool,
  onClick: PropTypes.func,
  hasCaret: PropTypes.bool,
  text: PropTypes.string,
  label: PropTypes.string,
  className: PropTypes.string,
  disableHover: PropTypes.bool
};

HeaderOption.defaultProps = {
  onClick: undefined,
  children: null,
  href: null,
  open: false,
  active: false,
  hasCaret: false,
  text: null,
  label: '',
  className: null,
  disableHover: false
};

const DesktopHeaderOptions = ({
  onClickDiscover,
  onClickGenres,
  onClickSearch,
  onClickLibrary,
  onClickProfile,
  onClickLogin,
  onClickOptions,
  genresOverlayOpen
}) => {
  const [moreOptionsExpanded, setMoreOptionsExpanded] = useState(false);
  const { isLoggedInWithProvider } = useUser();
  const activeRoutesObject = useActiveRoutes();
  const { user } = useUser();
  const { resolvedLanguage } = useLanguage();
  const { enableMultiLanguageSupport } = useLanguageEnabled();
  const { onClickChooseLanguage } = useHeaderBar();
  const { first_name, last_name } = user || {};
  const {
    discoverActive,
    libraryActive,
    profileActive,
    genresActive,
    searchActive
  } = activeRoutesObject;

  const handleClickMoreOptions = useCallback(() => {
    if (moreOptionsExpanded) {
      setMoreOptionsExpanded(false);
    } else {
      setMoreOptionsExpanded(true);
    }

    if (onClickOptions) {
      onClickOptions();
    }
  }, [onClickOptions, moreOptionsExpanded]);

  const handleClickDropDownOption = useCallback(({ id }) => {
    if (id === 'choose-language') {
      onClickChooseLanguage();
    }
  }, [onClickChooseLanguage]);

  const handleCloseMoreOptions = useCallback(() => {
    // Ensure that the closing of drop down happens after onBlur
    // This avoids the drop down failing to close when clicking on it
    setTimeout(() => setMoreOptionsExpanded(false), 100);
  }, []);

  const {
    ref,
    onBlur
  } = useFocusAndBlur({
    blurCallback: handleCloseMoreOptions,
    shouldFocus: moreOptionsExpanded
  });

  const dropDownOptions = useMemo(
    () => (
      resolvedLanguage
        ? createDropDownOptions()
          .filter(({ id }) => (enableMultiLanguageSupport ? true : (id !== CHOOSE_LANGUAGE_DROP_DOWN_ID)))
          .map(({ text, ...fields }) => ({
            ...fields,
            value: text
          }))
        : []
    ),
    [enableMultiLanguageSupport, resolvedLanguage]
  );

  return (
    <div className='desktop-header-options'>
      <HeaderOption
        active={ !genresOverlayOpen && discoverActive }
        href={ DISCOVER_KEY }
        label='Discover'
        onClick={ onClickDiscover }
        text='Discover'
      />
      <HeaderOption
        active={ genresOverlayOpen|| genresActive }
        hasCaret
        label='Genres'
        onClick={ onClickGenres }
        open={ genresOverlayOpen }
        text='Genres'
      />
      { !!isLoggedInWithProvider && (
        <HeaderOption
          active={ libraryActive }
          href={ LIBRARY_KEY }
          label='My library'
          onClick={ onClickLibrary }
          text='My library'
        />
      ) }
      <HeaderOption href={ pageKeys.search } label='Search'>
        <Span
          className={ classes('search-icon-wrapper', { '--is-active': !genresOverlayOpen && searchActive }) }
          onClick={ onClickSearch }
        >
          <SearchIcon />
        </Span>
      </HeaderOption>
      <HeaderOption
        className={ classes('header-option-more', { '--is-active': moreOptionsExpanded }) }
        disableHover
        label='Options'
        onBlur={ onBlur }
        onClick={ handleClickMoreOptions }
        ref={ ref }
        tabIndex={ 0 }
      >
        <Span
          className={ classes('more-options-icon-wrapper', { '--is-active': false }) }
          onClick={ onClickOptions }
        >
          <OptionTripleDotIcon />
        </Span>
        <div
          className='more-options-drop-down'
        >
          <DropDownSelectOptions
            onClickOption={ handleClickDropDownOption }
            onClose={ handleCloseMoreOptions }
            open={ moreOptionsExpanded }
            options={ dropDownOptions }
          />
        </div>
      </HeaderOption>
      { isLoggedInWithProvider ? (
        <HeaderOption
          active={ profileActive }
          href={ PROFILE_KEY }
          onClick={ onClickProfile }
        >
          <div className={ styles.profileWrapper }>
            <div className={ styles.userImageWrapper }>
              <ProfileImage assets={ user && user.assets } />
            </div>
            <Text
              bold
              className='header-option-content'
              fixed
            >
              { createNameString({
                first_name,
                last_name
              }) }
            </Text>
          </div>
        </HeaderOption>
      ) : (
        <SignInLink
          onClick={ onClickLogin }
          text='Sign in'
        />
      ) }
    </div>
  );
};

DesktopHeaderOptions.propTypes = {
  onClickDiscover: PropTypes.func.isRequired,
  onClickGenres: PropTypes.func.isRequired,
  onClickSearch: PropTypes.func.isRequired,
  onClickLibrary: PropTypes.func.isRequired,
  onClickProfile: PropTypes.func.isRequired,
  onClickLogin: PropTypes.func.isRequired,
  onClickOptions: PropTypes.func.isRequired,
  genresOverlayOpen: PropTypes.bool.isRequired
};

export default DesktopHeaderOptions;
